
import ResizeObserver from 'resize-observer-polyfill'

export class VideoElement {
  /** @type {?HTMLMediaElement} */
  _video

  /** the video's with as it was last time on calculateActiveArea call */
  _lastVideoWidth

  _lastVideoHeight

  constructor (
    calculateActiveArea = VideoElement._calculateActiveArea,
    ResizeObserverType = ResizeObserver
  ) {
    this._calculateActiveArea = calculateActiveArea
    this._activeArea = {
      left: 0, right: 0, top: 0, bottom: 0
    }
    this._ratio = { xRatio: 1, yRatio: 1 }
    // Zooming the browser does not trigger the ResizeObserver
    this._resizeObserver = new ResizeObserverType(this._videoOnResize)
    // We need this in order to catch the zoom event, which CANNOT be differentiated from a regular resize event
    window.addEventListener('resize', this._windowOnResize)
    // Due to this fact, the regular resize events will trigger both of the handlers,
    // resulting in useless calls of _calculateActiveArea

    this._checkVideoSizeChangesIntervalId = setInterval(this._checkVideoSizeChanges, 500)
  }

  finalize = () => {
    clearInterval(this._checkVideoSizeChangesIntervalId)
  }

  setVideo = (video) => {
    this._unsubscribe(this._video)
    this._subscribe(video)
    this._video = video
  }

  getRatio = () => {
    this._recalculateDimensions()
    return this._ratio
  }

  getActiveAreaRect = () => {
    return this._activeArea
  }

  /**
   * private
   */

  /**
   * @returns the dimensions of the stream in pixels
   * e.g, when capturing two monitors of 1080p, width will be 2 x 1920, height will be 1080
   */
  _getVideoDimensions = () => {
    const width = this._video ? this._video.videoWidth : 0
    const height = this._video ? this._video.videoHeight : 0

    return { width, height }
  }

  _checkVideoSizeChanges = () => {
    if (this._video) {
      const isSizeChanged = this._lastVideoWidth !== this._video.videoWidth || this._lastVideoHeight !== this._video.videoHeight
      if (isSizeChanged) {
        this._recalculateDimensions()
      }
    }
  }

  /**
   * this should be called on both window resize and zoom events
   */
  _windowOnResize = () => {
    if (this._video) {
      this._recalculateDimensions()
    }
  }

  _subscribe = (video) => {
    if (video) {
      video.addEventListener('loadedmetadata', this._videoOnLoadedmetadata)
      this._resizeObserver.observe(video)
    }
  }

  _unsubscribe = (video) => {
    if (video) {
      video.removeEventListener('loadedmetadata', this._videoOnLoadedmetadata)
      this._resizeObserver.unobserve(video)
    }
  }

  _videoOnLoadedmetadata = (/* event */) => {
    this._recalculateDimensions()
  }

  _videoOnResize = (entries, /* observer */) => {
    if (entries.length > 0) {
      this._recalculateDimensions()
    }
  }

  _recalculateDimensions = () => {
    if (this._video) {
      this._lastWidth = this._video.videoWidth
      this._lastHeight = this._video.videoHeight

      this._activeArea = this._calculateActiveArea(this._video)
      this._ratio = VideoElement._calculateRatio(this._activeArea, this._getVideoDimensions())
    }
  }

  /**
   * calculates the ratio of <remote desktop width> : <active area width> (same for height)
   * e.g.: remote desktop showing 2 monitors of 1080p in full screen =>
   * <remote desktop width> = 2 * 1920; <active area width> = 1920; => xRatio = 2
   */
  static _calculateRatio = (activeArea, remoteDesktopDimensions) => {
    const activeAreaWidth = activeArea.right - activeArea.left
    const xRatio = remoteDesktopDimensions.width / activeAreaWidth

    const activeAreaHeight = activeArea.bottom - activeArea.top
    const yRatio = remoteDesktopDimensions.height / activeAreaHeight

    return { xRatio, yRatio }
  }

  /**
   * @param {HTMLMediaElement} video
   */
  static _calculateActiveArea (video) {
    // Ratio of the video's intrisic dimensions
    const videoRatio = video.videoWidth / video.videoHeight
    // The width and height of the video element
    const width = video.offsetWidth
    const height = video.offsetHeight
    let bottom, left, right, top
    // The ratio of the element's width to its height
    const elementRatio = width / height

    if (elementRatio > videoRatio) {
      // If the video element is short and wide
      const activeWidth = height * videoRatio
      left = (width - activeWidth) / 2
      right = width - ((width - activeWidth) / 2)
      top = 0
      bottom = height

    } else {
      // It must be tall and thin, or exactly equal to the original ratio
      const activeHeight = width / videoRatio
      left = 0
      right = width
      top = (height - activeHeight) / 2
      bottom = height - ((height - activeHeight) / 2)
    }

    return { left, right, top, bottom }
  }
}
